import { gql, useMutation } from '@apollo/client'

import {
  CreateFavoriteMemberMutation,
  CreateFavoriteMemberMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import {
  useAuth,
  useCurrentUser,
  useMessage,
  useRouter,
  useTranslation,
} from '@/core'
import { useUserTmpUploader } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { Camera } from '@/core/native'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { showError } = useMessage()
  const { t } = useTranslation('consoleFavoriteMemberCreate')
  const { routerPages, router } = useRouter()
  const { reloadUser } = useAuth()
  const { getImage } = Camera
  const { upload, progress } = useUserTmpUploader(user?.uid ?? '')

  const [createFavoriteMember, { loading: creatingMember }] = useMutation<
    CreateFavoriteMemberMutation,
    CreateFavoriteMemberMutationVariables
  >(CREATE_FAVORITE_MEMBER_MUTATION)

  return {
    state: {
      creatingMember,
      artistId: user?.uid ?? '',
      progress,
    },
    actions: {
      async onSubmit(values) {
        await createFavoriteMember({
          variables: {
            inputs: {
              avatarPath: values.avatarPath,
              name: values.name,
            },
          },
        })

        await reloadUser()
        router.goBack(routerPages.ConsoleFavoriteMemberSettings)
      },
      async onDrop() {
        try {
          const file = await getImage(config.upload.mimeTypes.image)

          if (file.size > config.upload.limit['image']) {
            throw new Error('uploadErrorMaximumSize')
          }

          const { tmpPath, fileUrl } = await upload(file, false, true)
          const uuid = createUuid()

          return {
            id: uuid,
            resourceName: uuid,
            imageUploadPath: tmpPath,
            imageUrl: fileUrl,
          }
        } catch (error) {
          if (error instanceof Error) {
            if (error.message === 'uploadErrorMaximumSize') {
              showError(new Error(t('uploadErrorMaximumSize')))
            } else if (error.message === 'uploadErrorImageOnly') {
              showError(new Error(t('uploadErrorImageOnly')))
            } else {
              showError(error)
            }
          } else {
            showError(error as Error)
          }
          return null
        }
      },
    },
  }
})

const CREATE_FAVORITE_MEMBER_MUTATION = gql`
  mutation CreateFavoriteMember($inputs: ArtistCreateFavoriteMemberInput!) {
    artistCreateFavoriteMember(inputs: $inputs) {
      id
      name
      avatar {
        uri
      }
      orderNumber
    }
  }
`
